import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Router, { useRouter } from 'next/router';
import { GetStaticPaths, GetStaticProps } from 'next';

import * as Sentry from '@sentry/nextjs';
import { subMonths } from 'date-fns';
import dayjs from 'dayjs';

import Button from 'epn-ui/umd/dist/components/Button';
import Loader from 'epn-ui/umd/dist/components/Loader';

import { toast } from 'react-toastify';

import useI18n, { getLocalePaths, LocaleTypes } from '@i18n';

import api from '@api';
import { formatStatOffers } from '@apiHooks/formatters';
import sendGaEvent from 'src/utils/metrics/ga';

import Cards from '@pages/statistics/transactions/Cards';
import Filters from '@pages/statistics/transactions/Filters';
import Table from '@pages/statistics/transactions/Table';

import BaseToast from '@components/base/BaseToast';

import {
    asyncTransaction,
    exportTransactions,
    formatCreativesForSelect,
    formatOffersForSelect,
    getDefaultCurrency,
    prepareCards,
    prepareCurrencies,
    prepareSubsSelectOptions,
} from '@pages/statistics/transactions/utils';
import { getSumWithCurrency } from '@utils/formatters/d3Formatters';

import { useSummary, useTransactions } from '@pages/statistics/transactions/hooks';

import apiHooks, { ICreateUseRequestInfiniteResponse } from '@apiHooks';
import {
    cardsObj,
    datePeriods,
    dateRanges,
    DEFAULT_FILTERS,
    filtersConversionFuncs,
    tableColumns,
} from '@pages/statistics/transactions/config';

// HOCs
import WithRole, { rolesEnum } from '@HOCs/WithRole';
import {
    IGetCreativesDataFormatted,
    IGetOffersDataFormatted,
    IGetStatOffersData,
    IGetStatOffersDataFormatted,
    IGetSubsDataFormatted,
    IResponse,
} from '@api/types';
import useFilters from '@hooks/useFilters';
import {
    ITransactionOrderOption,
    ITransactionsCard,
    ITransactionsDate,
    ITransactionsFilterOption,
    ITransactionsFilterOptions,
    ITransactionsFilterValues,
    ITransactionsLoadings,
    ITransactionsTableData,
    IRange,
} from '@pages/statistics/transactions/interfaces';
import { CurrencyTypes, CustomGetStaticPathsResult, CustomGetStaticPropsResult } from 'src/types.d';
import { getPeriodByDates } from '@components/pages/statistics/common/utils';

const { useSubs, useCreatives, useCreativesBySearch } = apiHooks;

// Виды сортировок по умолчанию
const ORDER_BY_DATE = {
    order: 'stats_ts',
    orderDirection: 'desc',
};

const DEFAULT_SORT = { ...ORDER_BY_DATE };

const TransactionsStatistics: React.FC = () => {
    const { t, locale } = useI18n('Transactions');
    const { t: tStat } = useI18n('Statistic');
    const { t: tCommon } = useI18n('Common');

    const [creativesSearchValue, setCreativesSearchValue] = useState<string>('');
    const [exportInProgress, setExportInProgress] = useState<boolean>(false);
    const [sort, setSort] = useState<ITransactionOrderOption>(DEFAULT_SORT);
    /* Запрашиваем список офферов со статистикой */
    const [offersWithStats, setOffersWithStats] = useState<IGetOffersDataFormatted[]>([]);
    const [isLoadingOffers, setIsLoadingOffers] = useState<boolean>(true);
    // максимальный период год
    const [minMaxDateRange] = useState({
        minDate: subMonths(new Date(), 12),
        maxDate: new Date(),
    });
    const [datePeriod, setDatePeriod] = useState('');
    const { query, isReady } = useRouter();
    const { tsFrom: urlDateFrom, tsTo: urlDateTo } = query;

    const filterOptions = {
        conversionFuncs: filtersConversionFuncs,
        coupleKeys: ['tsFrom', 'tsTo'],
    };

    const filterParams: ITransactionsFilterValues = {
        ...DEFAULT_FILTERS,
        ...(query || {}),
        currency:
            CurrencyTypes[query.currency as string] ??
            (locale === LocaleTypes.ru ? CurrencyTypes.RUB : CurrencyTypes.USD),
    };

    const [filters, setFilters] = useFilters(filterParams, filterOptions);

    const filterValues: ITransactionsFilterValues = { ...filters, datePeriod };

    const offerIds: number[] = useMemo(
        () => offersWithStats.map((offer: IGetOffersDataFormatted) => offer.id),
        [offersWithStats],
    );
    const offerIdsAsString: string = useMemo(() => offerIds.join(','), [offerIds]);

    // Кастомный хук, который возвращает транзакции и состояния запросов
    const {
        data,
        refetch,
        isFetching,
        isFetchingMore,
        fetchMore,
        canFetchMore,
        error: transactionsError,
    } = useTransactions({
        filters: {
            ...filters,
            offerId: filters.offerId ?? offerIdsAsString,
            fields:
                'order_number,order_time,order_status,transaction_time,revenue,commission_user,creative_title,sub_title,user_click_id,offer_type,offer_id,currency,transactionId,product,product_link,type_id,advertForm',
        },
        sort,
    });

    const dataIsEmpty: boolean = data.transactions.length === 0;

    const {
        data: summary,
        isFetching: isFetchingSummary,
        refetch: refetchSummary,
        error: summaryError,
    } = useSummary({
        filters: {
            ...filters,
            offerId: filters.offerId || offerIdsAsString,
        },
    });

    const initDatePeriodFromUrl = useCallback(() => {
        if (!urlDateFrom || !urlDateTo) {
            setDatePeriod('week');
            return;
        }

        const period = getPeriodByDates(urlDateFrom as string, urlDateTo as string);

        setDatePeriod(period ?? '');
    }, [urlDateFrom, urlDateTo]);

    useEffect(() => {
        if (!isReady) return;

        initDatePeriodFromUrl();
    }, [isReady, initDatePeriodFromUrl, urlDateFrom, urlDateTo]);

    useEffect((): void => {
        const offerParams = { typeIds: '1,2,3,4,6,7', order: 'priority' };

        api.offer
            .getOffersStats({ params: offerParams })
            .then((res: IResponse<IGetStatOffersData[]>): void => {
                const resData: IGetStatOffersDataFormatted[] = formatStatOffers(res.data);

                setOffersWithStats(resData);

                // Если офферы есть и оффер не установлен, то выставляем первый оффер в фильтры и соответсвующую валюту
                if (resData.length !== 0) {
                    const {
                        offerId: offerIdFromRouterQuery,
                        tsFrom: tsFromFromRouterQuery,
                        tsTo: tsToFromRouterQuery,
                        currency: currencyFromParams,
                    } = Router.router?.query;

                    // Вытаскиваем первый оффер
                    const currentOfferId: number = Number(offerIdFromRouterQuery) || resData[0].id;
                    const currencyByLocal =
                        locale === LocaleTypes.ru ? CurrencyTypes.RUB : CurrencyTypes.USD;
                    const availableCurrencies: ITransactionsFilterOption<string>[] =
                        currentOfferId === null
                            ? prepareCurrencies(null, resData)
                            : prepareCurrencies(currentOfferId, resData);

                    // Получаем предвыбранную валюту по приоритету:
                    // 1) из параметров урла
                    // 2) по локали
                    // 3) дефолтную первую из доступных
                    const currency: string = getDefaultCurrency(
                        availableCurrencies,
                        currencyFromParams?.toString(),
                        currencyByLocal,
                    );

                    const respOfferIds: number[] = resData.map(
                        (offer: IGetOffersDataFormatted) => offer.id,
                    );
                    const respOfferIdsAsString = respOfferIds.join(',');

                    const newFilters: ITransactionsFilterValues = {
                        ...filters,
                        ...(Router.router?.query || {}),
                        tsFrom:
                            tsFromFromRouterQuery && typeof tsFromFromRouterQuery === 'string'
                                ? dayjs(tsFromFromRouterQuery).format('MM-DD-YYYY').toString()
                                : filters.tsFrom,
                        tsTo:
                            tsToFromRouterQuery && typeof tsToFromRouterQuery === 'string'
                                ? dayjs(tsToFromRouterQuery).format('MM-DD-YYYY').toString()
                                : filters.tsTo,
                        currency: CurrencyTypes[(currencyFromParams ?? currency) as string],
                    };

                    // TODO: Хак, текущая архитектура не подходила под правки, надо улучшить. ТЕХДОЛГ
                    setFilters(newFilters);
                    refetch({ ...newFilters, offerIds: respOfferIdsAsString });
                    refetchSummary({ ...newFilters, offerIds: respOfferIdsAsString });
                }
            })
            .catch(error => {
                // eslint-disable-next-line
                console.log(error);
            })
            .finally((): void => {
                setIsLoadingOffers(false);
            });
        // eslint-disable-next-line
    }, [locale, setIsLoadingOffers]);

    const subsParams = { subFields: 'sub,sub2,sub3,sub4,sub5' };

    /* Запрашиваем список сабов */
    const { data: subsData = [], isLoading: isLoadingSubs } = useSubs({
        params: subsParams,
        condition: !!filters.offerId || !!offerIdsAsString,
        dependencies: [locale],
    }) as ICreateUseRequestInfiniteResponse<IGetSubsDataFormatted[]>;

    const firstOffersValue = useMemo(
        (): ITransactionsFilterOption<number> => ({
            value: null,
            label: tStat('All offers'),
        }),
        [tStat],
    );

    // options для селектов
    const offersSelectOptions = useMemo(
        () => [firstOffersValue, ...formatOffersForSelect(offersWithStats)],
        [firstOffersValue, offersWithStats],
    );

    // Валюты пересчитываются если меняется оффер
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const currencies = useMemo(() => {
        return prepareCurrencies(filters.offerId, offersWithStats);
    }, [filters.offerId, offersWithStats]);

    const {
        data: creativesData,
        nextPage: nextPageCreatives,
        isLoading: isLoadingRegularCreatives,
        isReachingEnd: isReachingEndCreatives,
    } = useCreatives({
        params: {
            fields: 'id,description,offerId',
            offersIds: filters.offerId || offerIdsAsString,
            isDeleted: false,
            limit: 20,
        },
        condition: !!filters.offerId || offerIdsAsString !== '',
        dependencies: [locale, filters.offerId, offerIdsAsString],
    }) as ICreateUseRequestInfiniteResponse<IGetCreativesDataFormatted[]>;

    const creativesParams = {
        name: creativesSearchValue,
        isDeleted: false,
        offersIds: filters.offerId || offerIdsAsString,
        fields: 'id,description,offerId',
        limit: 100,
        search: creativesSearchValue ?? '',
    };

    const { data: creativesBySearch, isLoading: isLoadingCreativesBySearch } = useCreativesBySearch(
        {
            params: creativesParams,
            dependencies: [creativesSearchValue],
            condition: !!creativesSearchValue,
        },
    ) as ICreateUseRequestInfiniteResponse<IGetCreativesDataFormatted[]>;

    const firstCreativeValue: ITransactionsFilterOption<string> = {
        value: null,
        label: tStat('All creatives'),
    };

    const filteredCreativesData: IGetCreativesDataFormatted[] = creativesData.filter(
        ({ offerId }) =>
            filters.offerIds ? Number(filters.offerIds) === Number(offerId) : offerId,
    );

    const filteredCreativesBySearchData: IGetCreativesDataFormatted[] = creativesBySearch.filter(
        ({ offerId }) =>
            filters.offerIds ? Number(filters.offerIds) === Number(offerId) : offerId,
    );

    const creativesSelectOptions: ITransactionsFilterOption<string>[] = useMemo(
        () => {
            const needCreativesBySearch = creativesSearchValue !== '';
            const filteredCreativesBySearch = needCreativesBySearch
                ? formatCreativesForSelect(filteredCreativesBySearchData)
                : [];
            return [
                firstCreativeValue,
                ...filteredCreativesBySearch,
                ...formatCreativesForSelect(filteredCreativesData),
            ];
        },
        // eslint-disable-next-line
        [creativesData, creativesBySearch, creativesSearchValue],
    );

    const subsSelectOptions: ITransactionsFilterOption<string | null>[] = useMemo(
        () => prepareSubsSelectOptions(subsData),
        [subsData],
    );

    const firstSubsValue: ITransactionsFilterOption<string | null> = {
        value: null,
        label: t('All subs'),
    };

    const translatedDateRanges: ITransactionsDate[] = useMemo(
        () =>
            dateRanges.map(dateRange => ({
                ...dateRange,
                label: t(dateRange.key),
                id: dateRange.key,
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [locale],
    );

    const options: ITransactionsFilterOptions = {
        offers: offersSelectOptions,
        creatives: creativesSelectOptions,
        dateRanges: translatedDateRanges,
        subs: [firstSubsValue, ...subsSelectOptions],
        currencies,
    };

    const loadings: ITransactionsLoadings = {
        isLoadingOffers,
        isLoadingCreatives: isLoadingRegularCreatives || isLoadingCreativesBySearch,
        isLoadingSubs,
    };

    const onDateRangeChange = (dateType: string, range?: IRange): void => {
        const { tsFrom, tsTo } = datePeriods[dateType] || range;
        setFilters({ ...filters, tsFrom, tsTo });
    };

    const filtersOnChange = (name, value): void => {
        const { dateFrom: tsFrom, dateTo: tsTo, value: selectValue } = value || {};
        const isRadioBtnOrInput = typeof value === 'string';
        let newValue = null;
        const fieldName = name;

        if (name === 'dateRange') {
            setFilters({ ...filters, tsFrom, tsTo });
            return;
        }

        if (selectValue) newValue = selectValue;

        if (isRadioBtnOrInput) newValue = value;

        let resultValue = { ...filters, [fieldName]: newValue };

        if (name === 'offerId') {
            const offerId = selectValue;
            const currencyByLocal =
                locale === LocaleTypes.ru
                    ? CurrencyTypes.RUB.toString()
                    : CurrencyTypes.USD.toString();
            const { currency: currencyFromParams } = Router.router?.query;

            const availableCurrencies: ITransactionsFilterOption<string>[] =
                offerId === null
                    ? prepareCurrencies(null, offersWithStats)
                    : prepareCurrencies(offerId, offersWithStats);

            const currency: string = getDefaultCurrency(
                availableCurrencies,
                currencyFromParams?.toString(),
                currencyByLocal,
            );

            resultValue = {
                ...resultValue,
                currency: CurrencyTypes[currency],
                creativeId: null,
            };
        }

        setFilters(resultValue);
    };

    const exportOnClick = useCallback(async (): Promise<void> => {
        if (dataIsEmpty) {
            toast.warning(<BaseToast type="warn" text={tStat('No data available')} />, {
                hideProgressBar: true,
                closeButton: false,
            });
            return;
        }

        // Проверка на максимальное кол-во выгрузки
        if (data.totalFound < 100000) {
            try {
                setExportInProgress(true);
                toast.info(
                    <BaseToast type="info" text={t('Started to form the export, please wait')} />,
                    {
                        hideProgressBar: true,
                    },
                );
                // Уведомление о начале выгрузки
                if (data.totalFound < 1000) {
                    await exportTransactions(filters);
                } else {
                    await asyncTransaction(filters);
                    toast.info(
                        <BaseToast
                            type="info"
                            text={t(
                                'The download is being generated and will be sent to your email',
                            )}
                        />,
                        {
                            hideProgressBar: true,
                        },
                    );
                }
                // Логируем успешную выгрузку
                sendGaEvent({
                    eventCategory: 'transactions',
                    eventAction: 'export',
                    eventLabel: 'offer_id',
                    eventValue: +filters.offerId,
                });
            } catch (error) {
                toast.error(
                    <BaseToast type="error" text={t('Error while exporting. Try again later')} />,
                    {
                        hideProgressBar: true,
                    },
                );
                console.error(error);
                Sentry.captureException(error);
            } finally {
                setExportInProgress(false);
            }
        } else {
            toast.error(
                <BaseToast
                    type="error"
                    text={t('The unloading volume is too large, reduce the period')}
                />,
                {
                    hideProgressBar: true,
                    closeButton: false,
                },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataIsEmpty, filters, t]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const cards: ITransactionsCard[] = useMemo(
        () => prepareCards(cardsObj, summary, filters.currency),
        // eslint-disable-next-line
        [summary],
    );

    // Добавляем валюту к данным таблицы
    const dataWithCurrency: ITransactionsTableData[] = useMemo(
        () =>
            data.transactions.map(item => ({
                ...item,
                revenue: getSumWithCurrency(item.revenue, filters.currency) as string,
                commissionUser: getSumWithCurrency(item.commissionUser, filters.currency),
                offerName:
                    filters.offerId === null &&
                    offersWithStats.find(offer => offer.id === +item.offerId)?.name,
            })),
        // eslint-disable-next-line
        [data, offersWithStats, filters.offerId],
    );

    /**
     * Функция для поиска креатива по введеннным в селектор значениям
     */
    const onSearchCreatives = (searchValue: string): void => {
        if (searchValue.length > 2) {
            setCreativesSearchValue(searchValue);
        }
    };

    return (
        <>
            <h3 className="mb-6 font-medium text-h2 sm:text-h1 text-blueGray-800">
                {t('Transactions')}
            </h3>
            <Filters
                values={filterValues}
                options={options}
                loadings={loadings}
                onChange={filtersOnChange}
                onButtonClick={() => {
                    // Сбрасываем сортировку, чтобы сработал хук запроса
                    setSort({ ...DEFAULT_SORT });
                    // Summary не зависит от сортировки, поэтому запрашиваем вручную
                    refetchSummary();
                }}
                onDateRangeChange={onDateRangeChange}
                minMaxDateRange={minMaxDateRange}
                exportOnClick={exportOnClick}
                exportInProgress={exportInProgress}
                onSearch={onSearchCreatives}
                nextPageCreatives={() =>
                    !isReachingEndCreatives &&
                    (!isLoadingCreativesBySearch || isLoadingRegularCreatives) &&
                    nextPageCreatives()
                }
            />

            {summaryError ? (
                <p className="mt-12 mb-12 text-center">
                    {tStat('Failed to load summary statistics')}
                    <br />
                    <b>{summaryError}</b>
                </p>
            ) : (
                <Cards cards={cards} isFetching={isFetchingSummary} />
            )}

            {isFetching && (
                <div className="flex justify-center mt-10 mb-10">
                    <Loader />
                </div>
            )}

            {!isFetching &&
                !transactionsError &&
                (dataIsEmpty ? (
                    <p className="mt-12 mb-12 text-center text-blueGray-800">
                        {tStat('No data available')}
                    </p>
                ) : (
                    <Table
                        columns={tableColumns}
                        data={dataWithCurrency}
                        onSort={setSort}
                        orderBy={sort}
                    />
                ))}

            {transactionsError && (
                <p className="mt-12 mb-12 text-center">
                    {tStat('Failed to load statistics')}
                    <br />
                    <b>{transactionsError}</b>
                </p>
            )}

            <div className="flex justify-center pt-10 mb-10">
                {isFetchingMore && <Loader />}
                {canFetchMore && !isFetchingMore && !isFetching && (
                    <Button size="middle" onClick={fetchMore}>
                        {tCommon('Load more')}
                    </Button>
                )}
            </div>
        </>
    );
};

export const getStaticProps: GetStaticProps = async ({
    params,
}): Promise<CustomGetStaticPropsResult> => {
    return { props: { locale: params?.locale } };
};

export const getStaticPaths: GetStaticPaths = async (): Promise<CustomGetStaticPathsResult> => {
    return { paths: getLocalePaths(), fallback: false };
};

export default WithRole(TransactionsStatistics, [rolesEnum.user]);
